// Must be imported before the app renders so breadcrumbs/navigations are
// captured from the first frame.
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const environment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
  (import.meta.env.PROD ? "production" : "development");

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // AdMoji surfaces ad creative + client brand data; default to locked-down replay
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    // Propagate trace headers only to our own API
    tracePropagationTargets: [/^\/api\//, /^https:\/\/admoji\./],
    replaysSessionSampleRate: environment === "production" ? 0.05 : 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Capture an error to Sentry and log it. Use at async boundaries where
 * errors would otherwise be silently swallowed (effects, event handlers,
 * fire-and-forget fetches).
 */
export function captureError(err: unknown, context?: Record<string, unknown>) {
  const error = err instanceof Error ? err : new Error(String(err));
  if (dsn) {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  }
  // eslint-disable-next-line no-console
  console.error("[captureError]", context?.source ?? "", error);
}

export { Sentry };
